"use client";
import React from "react";
import { useState, useEffect, useRef } from "react";
import { SelectProps } from "./types";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import { TiTimes } from "react-icons/ti";

const Select: React.FC<SelectProps> = ({
  label,
  required = false,
  placeholder,
  inputW,
  noBorder,
  options,
  value = "",
  onChange,
  borderStyle,
  dropdownItemStyle,
  onClick,
  clickable = false,
  dropdownStyle,
  cancelStyle,
  inputParentStyle,
  className,
  readonly,
  name,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value with local state
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Close dropdown when clicked outside
  useOutsideClick(dropdownRef, () => setDropdownOpen(false));

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle selecting an option
  const handleSelect = (option: string) => {
    setSelectedValue(option);
    setSearchTerm(""); // Clear search after selection
    setDropdownOpen(false);
    onChange?.(option); // Notify parent with the selected option
  };

  // Handle search input changes (no parent notification)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setSelectedValue(""); // Clear selection to enable search
    setDropdownOpen(true); // Ensure dropdown opens during typing
  };

  // Clear selected value or reset search term on cancel
  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchTerm(""); // Reset search term
    setSelectedValue(""); // Reset selected value
    onChange?.(""); // Notify parent that no value is selected
    setDropdownOpen(false); // Close dropdown after clearing
  };

  // Toggle dropdown open state based on the arrow clicked
  const toggleDropdown = (direction: "up" | "down") => {
    if (direction === "down") {
      setDropdownOpen(true); // Open dropdown on arrow down click
    } else {
      setDropdownOpen(false); // Close dropdown on arrow up click
    }
  };

  // Open dropdown when input is focused and no value is selected
  const handleFocus = () => {
    if (!selectedValue && !searchTerm) {
      setDropdownOpen(true); // Open dropdown when input is focused and no value is selected
    }
  };

  return (
    <div className={`relative h-full ${className}`} ref={dropdownRef}>
      <label className="block ">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className={`relative h-full`}>
        <div
          className={`flex items-center h-full shadow text-sm appearance-none ${
            !noBorder ? "bg-label-main border-label-main " : "border-none"
          } border  rounded-md bg-white  text-secondary-main shadow-none !pl-0 font-medium p-2 py-2 leading-tight focus:outline-none ${inputParentStyle}  ${borderStyle}`}
          tabIndex={0}
          aria-expanded={isDropdownOpen}
        >
          <input
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
            ref={inputRef}
            name={name}
            type="text"
            id="search-text"
            style={{
              width: inputW,
            }}
            placeholder={placeholder}
            className=" placeholder:text-sm flex-1 w-max inline-block h-full p-1 placeholder:font-normal placeholder:text-primary  outline-none"
            value={searchTerm || selectedValue} // Shows search term or selected value
            onChange={handleInputChange}
            onFocus={handleFocus} // Open dropdown when input is focused
            readOnly={!!selectedValue || clickable} // Prevent typing if a value is selected
          />
          <div className="items">
            {selectedValue ? (
              <button
                onClick={(e) => {
                  if (onClick) {
                    onClick();
                  }
                  if (readonly) {
                    return;
                  }
                  handleClear(e);
                }}
                type="button"
                className={` text-white flex items-center justify-center bg-black border-label-main  size-4 rounded-full  ${cancelStyle}`}
              >
                <TiTimes />
              </button>
            ) : (
              <button
                type="button"
                className=""
                onClick={() => {
                  if (onClick) {
                    onClick();
                  }
                  toggleDropdown(isDropdownOpen ? "up" : "down");
                }}
              >
                <span
                  className={`transition-all block transform duration-500 ${
                    !isDropdownOpen ? "rotate-0" : "rotate-[180deg]"
                  }`}
                >
                  <ArrowDown />
                </span>
              </button>
            )}
          </div>
        </div>
        {isDropdownOpen && (
          <ul
            className={`absolute z-[20] scrollbar-hide bg-white border rounded shadow-lg max-h-48 w-[200px] sm:!left-0 right-0 overflow-auto mt-1 ${dropdownStyle}`}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={index}
                  className={`px-4 text-sm py-2 hover:bg-gray-200 capitalize cursor-pointer ${dropdownItemStyle}`}
                  onClick={() => handleSelect(option)} // Notify parent on item select
                >
                  {option}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No matches found</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Select;

export const ArrowDown = () => {
  return (
    <svg
      width="14"
      height="9"
      viewBox="0 0 14 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1780_8299)">
        <path
          d="M2.15122 1.45104C2.09553 1.39841 2.03002 1.35727 1.95843 1.32996C1.88683 1.30265 1.81056 1.28971 1.73397 1.29188C1.65738 1.29405 1.58196 1.31128 1.51203 1.34259C1.4421 1.3739 1.37901 1.41868 1.32639 1.47437C1.27376 1.53007 1.23262 1.59558 1.20531 1.66717C1.178 1.73876 1.16506 1.81503 1.16723 1.89162C1.16939 1.96822 1.18662 2.04363 1.21794 2.11357C1.24925 2.1835 1.29403 2.24658 1.34972 2.29921L6.59972 7.25754C6.70803 7.35994 6.85142 7.41699 7.00047 7.41699C7.14952 7.41699 7.29291 7.35994 7.40122 7.25754L12.6518 2.29921C12.7087 2.24693 12.7547 2.18386 12.787 2.11367C12.8193 2.04348 12.8374 1.96757 12.8401 1.89034C12.8429 1.8131 12.8303 1.7361 12.803 1.66379C12.7757 1.59148 12.7344 1.52531 12.6813 1.46913C12.6282 1.41294 12.5645 1.36785 12.4939 1.33649C12.4233 1.30513 12.3471 1.28811 12.2699 1.28643C12.1926 1.28474 12.1158 1.29843 12.0439 1.32669C11.9719 1.35495 11.9063 1.39722 11.8509 1.45104L7.00047 6.03138L2.15122 1.45104Z"
          fill="#010A23"
        />
      </g>
      <defs>
        <clipPath id="clip0_1780_8299">
          <rect
            width="8.75"
            height="14"
            fill="white"
            transform="matrix(0 -1 1 0 0 8.875)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
